import React from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { compose, branch } from '../../node_module_clones/recompose';
import { identity } from 'lodash/fp';

// Selectors
import workspaceSelector from './workspaceSelector';
import { selectIsSplashVisible } from '../auth/modals/splash/AppSplash';

// Components
import WorkspaceContainer from './WorkspaceContainer';
import RouteModalManager from '../components/modal/RouteModalManager';

import workspacePageLoadingStateDecorator from './decorators/workspacePageLoadingStateDecorator';

import workspaceCurrentBoardFetchDecorator from './decorators/workspaceCurrentBoardFetchDecorator';
import workspaceNavigationDecorator from './decorators/workspaceNavigationDecorator';
import workspaceBreadcrumbDecorator from './breadcrumbs/workspaceBreadcrumbDecorator';
import UrlQueryElementFilterSynchroniser from '../element/elementFilter/UrlQueryElementFilterSynchroniser';
import UrlBoardPermissionIdSynchroniser from './permissions/UrlBoardPermissionIdSynchroniser';
import UrlPermissionTokenSynchroniser from './permissions/UrlPermissionTokenSynchroniser';
import PermissionAnalyticsOnInitialisation from './permissions/PermissionAnalyticsOnInitialisation';
import SocketConnectionStatusError from '../components/error/SocketConnectionStatusError';
import WorkspaceInitialisationManager from './initialisation/WorkspaceInitialisationManager';
import WorkspaceRootRedirectionManager from './decorators/WorkspaceRootRedirectionManager';
import InitialPageLoader from '../components/loaders/InitialPageLoader';
import { VirtualKeyboardHeightProvider } from '../utils/keyboard/VirtualKeyboardHeightContext';

/* ----
 * WorkspaceInitContainer
 * This component is the top level of the workspace stack,
 * it's responsible for fetching data and displaying and route-based modals
 */

// the decorators responsible for board data should only
// be used after the user is initialised
const boardLoadManagerEnhance = branch(
    ({ isUserAvailable }) => !!isUserAvailable,
    compose(workspaceCurrentBoardFetchDecorator, workspaceBreadcrumbDecorator, workspaceNavigationDecorator),
    identity,
);

// this handles loading of board and user navigation
// using conditional decorators that only attach once
const BoardLoadManager = boardLoadManagerEnhance(() => null);

const workspaceInitEnhance = compose(
    // select all data needed by the workspace
    connect(workspaceSelector),
    // keep the loader around until everything is ready to show
    workspacePageLoadingStateDecorator,
);

const WorkspaceInitContainer = (props) => {
    const { children, location, isGuest, showLoader, isUserAvailable, currentBoard, routes } = props;

    // If the mobile splash screen is showing (in AppInitContainer), some components here need to be hidden
    // TODO (WEB-12356) Split up WorkspaceInitContainer so that it doesn't need to know about the splash screen
    const isSplashVisible = useSelector((state) => selectIsSplashVisible(state, location));

    return (
        <VirtualKeyboardHeightProvider>
            <div className="WorkspaceInitContainer">
                <WorkspaceInitialisationManager isGuest={isGuest} />
                <UrlQueryElementFilterSynchroniser location={location} />

                {/* redirect '/' route to '/ROOT_BOARD_ID' route or /login */}
                <WorkspaceRootRedirectionManager
                    currentBoard={currentBoard}
                    isGuest={isGuest}
                    location={location}
                    isUserAvailable={isUserAvailable}
                />

                <UrlBoardPermissionIdSynchroniser currentBoardId={props.currentBoardId} />
                <UrlPermissionTokenSynchroniser location={location} />
                <PermissionAnalyticsOnInitialisation currentBoardId={props.currentBoardId} />

                <BoardLoadManager {...props} />

                {!isSplashVisible && <WorkspaceContainer {...props} />}

                <RouteModalManager routes={routes}>{children}</RouteModalManager>

                {showLoader && !isSplashVisible && <InitialPageLoader />}

                {/* Must sit above the modals in the DOM stacking context to ensure it can cover modals */}
                <SocketConnectionStatusError />
            </div>
        </VirtualKeyboardHeightProvider>
    );
};

WorkspaceInitContainer.propTypes = {
    currentBoardId: PropTypes.string,
    currentBoard: PropTypes.object,
    isGuest: PropTypes.bool,
    isUserAvailable: PropTypes.bool,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    location: PropTypes.object,

    showLoader: PropTypes.bool,

    socketConnection: PropTypes.object,
    socketConnectionIsInitialised: PropTypes.bool,

    routes: PropTypes.array,
};

export default workspaceInitEnhance(WorkspaceInitContainer);
